<template>
  <!-- Top Bar -->
  <TopBarInfo/>

  <!-- Nav Bar -->
  <nav class="navbar navbar-expand-xl">
    <div class="container">
      <MyLink class="navbar-brand" to="/">
        <img class="logo" :src="getLogoSrc()" :alt="getLogoDescription()" />
      </MyLink>
      <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav"
        aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
        <span class="navbar-toggler-icon"></span>
      </button>
      <div class="collapse navbar-collapse" id="navbarNav">
        <ul class="navbar-nav ms-auto">
          <template v-for="(item, index) in headerItems" :key="index">
            <li class="nav-item" v-if="item.subItems.length == 0">
              <template v-if="item.internalLink">
                <InternalLink class="nav-link" exact-active-class="active" :model="item.internalLink" :text="item.title" />
              </template>
              <template v-else>
                <MyLink :to="item.url" class="nav-link" exact-active-class="active" :target="safeLinkTarget(false, item.url)">
                  {{ item.title }}</MyLink>
              </template>
            </li>
            <li class="nav-item dropdown" v-if="item.subItems.length > 0">
              <a class="nav-link dropdown-toggle" href="/" role="button" data-bs-toggle="dropdown"
                aria-expanded="false">{{ item.title }}
                <font-awesome class="icon-caret" icon="chevron-down" />
              </a>
              <ul class="dropdown-menu">
                <li v-for="subItem in item.subItems">
                  <template v-if="subItem.internalLink">
                    <InternalLink class="nav-link" exact-active-class="active" :model="subItem.internalLink" :text="subItem.title" />
                  </template>
                  <template v-else>
                      <MyLink :external="true" class="dropdown-item" :to="subItem.url" exact-active-class="active" :target="safeLinkTarget(false, subItem.url)">
                        {{ subItem.title }}
                      </MyLink>
                  </template>
                </li>
              </ul>
            </li>
          </template>
          <template v-if="languages.length > 1">
            <li class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="/" role="button" data-bs-toggle="dropdown"
                aria-expanded="false">
                <font-awesome class="icon-globe" icon="globe" />
                {{ currentLocale?.name }}
                <font-awesome class="icon-caret" icon="chevron-down" />
              </a>
              <ul class="dropdown-menu">
                <li v-for="language in languages">
                  <NuxtLink :external="true" class="nav-link" :to="switchLocalePath(language.code as any)">
                    {{  language?.name }}
                  </NuxtLink>
                </li>
              </ul>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { contentTypes, type InternalLinkModel } from '~/models';
const siteStore = useSiteStore()
const route = useRoute()
const { locale } = useI18n()
const switchLocalePath = useSwitchLocalePath()

watch(locale, () => {
  siteStore.switchLanguage(locale.value)
})

const { activeGroup, navigationItems } = useNavigationGroup()

// Prepend the Home Page nav item for the active group (if available), avoiding duplicates
const headerItems = computed(() => {
  const rawItems: any[] = navigationItems.value || []
  const resolveInternalLink = (raw: any) =>
    raw?.internalLink?.length
      ? siteStore.getInternalLinkByCodename(raw.internalLink[0]) as InternalLinkModel
      : null

  const items = rawItems.map((item: any) => ({
    ...item,
    internalLink: resolveInternalLink(item),
    subItems: (item.subItems || []).map((sub: any) => ({
      ...sub,
      internalLink: resolveInternalLink(sub)
    }))
  }))

  const homeUrl = homepageUrl.value
  const exists = items.some((i: any) => i.url === homeUrl)
  if (exists || !homeUrl) return items
  const title = activeGroup.value?.homepageTitle || 'Home'

  return [
    { title, url: homeUrl, type: 'homepage', subItems: [], internalLink: null },
    ...items
  ]
})

const currentLocale = computed(() => siteStore.languages.find(l => l.code === locale.value))
const logo = firstOrDefault(siteStore.siteConfig.elements.websiteLogo.value);

const languages = computed(() => {
  return siteStore.languages
})

// Use the active group's homepage when available, fallback to locale root
const homepageUrl = computed(() => activeGroup.value?.homepageUrl || ('/' + (locale.value === siteStore.defaultLanguage ? '' : locale.value)))

function getLogoSrc() {
  if (logo) {
    return logo?.url
  }

  return '@/assets/img/logo-jnd.png';
}

function getLogoDescription() {
  if (logo) {
    return logo.description || 'JND Logo'
  }

  return 'JND Logo'
}

function safeLinkTarget(external: boolean, url?: string) {
  if (!url) return '_self';
  return getLinkTarget(external, url);
}
</script>
